import React from "react"

import { graphql } from "gatsby"
import PropTypes from "prop-types"

import Seo from "../components/head"
import Layout from "../components/layout"
import BackTo from "../components/molecules/back-to"
import StoryItem from "../components/molecules/story-item"
import Sidebar from "../components/organisms/sidebar"
import PortabletextToPlain from "../helpers/portabletext-to-plain"

const title = (data) =>
  data.sanityArticle
    ? data.sanityArticle.detailed_title || data.sanityArticle.title
    : `${
        data.sanityFaqGlossary.sport?.name ||
        data.allSanityDictionary.nodes.find(
          (dictionaryItem) => dictionaryItem.key.current === "general"
        )?.value
      } ${
        data.allSanityDictionary.nodes.find(
          (item) => item.key.current === data.sanityFaqGlossary.type
        )?.value
      }`

const ArticleTemplate = ({ data, pageContext }) => {
  const regionGuides = data.guides.nodes.filter(
    (post) =>
      post.country === null || post.country.abbreviation === pageContext.country
  )
  const sport = data.sanityArticle
    ? data.sanityArticle.sport[0]
    : data.sanityFaqGlossary.sport
  const sidebar =
    data.general_guides.nodes.length + data.general_faqglossary.nodes.length > 0
      ? data.general_guides.nodes.concat(data.general_faqglossary.nodes)
      : [...data.allSanityFaqGlossary.nodes, ...regionGuides]
  const sportSlug =
    data.sanityArticle?.web_article_type === "newsroom"
      ? null
      : sport?.slug.current || "general"
  const sportName =
    sport?.name ||
    data.allSanityDictionary.nodes.find(
      (item) => item.key.current === "general"
    )?.value

  const sanitySubscribe =
    data.allSanitySubscribe.nodes.find(
      (subscribe) => subscribe.vertical.slug.current === pageContext.vertical
    ) ||
    data.allSanitySubscribe.nodes.find(
      (subscribe) => subscribe.vertical.slug.current === "daily-sports"
    )

  return (
    <Layout pageType="article" currentVertical={pageContext.vertical || null}>
      <StoryItem
        title={title(data)}
        content={data.sanityArticle || data.sanityFaqGlossary}
        subscribe={sanitySubscribe}
      />
      <Sidebar
        color={sport?.color}
        title="learn_more"
        secondaryTitle={sportName}
        sidebar={sidebar}
      />
      {data.sanityArticle?.web_article_type === "newsroom" ? (
        <BackTo
          url="/newsroom"
          name={
            data.allSanityDictionary.nodes.find(
              (item) => item.key.current === "newsroom"
            )?.value
          }
        />
      ) : (
        <BackTo url={`/sport/${sportSlug}`} name={sportName} />
      )}
    </Layout>
  )
}

ArticleTemplate.propTypes = {
  data: PropTypes.object.isRequired,
  pageContext: PropTypes.object.isRequired,
}

export default ArticleTemplate

const HeadComponent = ({ data, location }) => {
  const content = data.sanityArticle || data.sanityFaqGlossary
  const faqData =
    data.sanityFaqGlossary?.type === "faq"
      ? `{
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": [${data.sanityFaqGlossary.questions.map(
          (item) => `{
          "@type": "Question",
          "name": "${item.question}",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "${PortabletextToPlain(item._rawAnswer).replaceAll(
              '"',
              "'"
            )}"
          }}`
        )}]
      }`
      : null
  const structuredData =
    faqData ||
    `{
    "@context": "https://schema.org",
    "@type": "NewsArticle",
    "headline": "${title(data).replaceAll('"', "&quot;")}",
    "speakable":
    {
      "@type": "SpeakableSpecification",
      "xPath": [
        "/html/head/meta[@property='og:title']/@content",
        "/html/head/meta[@name='description']/@content"
      ]
    },
    ${
      content.featured_image?.image?.asset
        ? `"image": ["${data.site.siteMetadata.siteUrl}${content.featured_image.image.asset.publicUrl}"],`
        : ""
    }
    ${content.date ? `"datePublished": "${content.date}",` : ""}
    "author": [
      {
        "@type": "Organization",
        "name": "${
          data.allSanityDictionary.nodes.find(
            (item) => item.key.current === "title"
          )?.value
        }"
      }
    ]
  }`

  return (
    <Seo
      title={title(data)}
      path={location.pathname}
      isAd={!(data.sanityArticle?.partner || data.sanityFaqGlossary?.partner)}
      image={
        content.featured_image?.image
          ? `${data.site.siteMetadata.siteUrl}${content.featured_image.image.asset.publicUrl}`
          : null
      }
      description={content.abstract}
      structuredData={structuredData}
    />
  )
}

HeadComponent.propTypes = {
  data: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
}

export const Head = HeadComponent

export const pageQuery = graphql`
  query articleQuery(
    $id: String!
    $sport: String!
    $general_guides: [String]!
    $general_faqglossary: [String]!
  ) {
    site {
      siteMetadata {
        siteUrl
      }
    }
    allSanityDictionary {
      nodes {
        key {
          current
        }
        value
      }
    }
    allSanitySubscribe {
      nodes {
        vertical {
          cm_id
          slug {
            current
          }
        }
        illustration {
          default {
            image {
              ...ImageWithPreview
            }
          }
        }
        additional_fields {
          type
        }
        gating {
          title
          _rawCopy
        }
      }
    }
    sanityFaqGlossary(id: { eq: $id }) {
      featured_image {
        ...ImageWithPreview
        asset {
          publicUrl
        }
      }
      type
      abstract
      sport {
        name
        color
        slug {
          current
        }
      }
      questions {
        question
        _rawAnswer
      }
      terms {
        term
        _rawDefinition
      }
    }
    sanityArticle(id: { eq: $id }) {
      title
      detailed_title
      slug {
        current
      }
      date
      vertical {
        name
        color
        slug {
          current
        }
      }
      is_ss_se
      editorial_color
      additional_article_sections {
        title
        visible
        partner {
          name
          url
          prefix
          logo {
            ...ImageWithPreview
          }
        }
        partner_url
        featured_image {
          image {
            ...ImageWithPreview
          }
          caption
        }
        _rawContent
      }
      abstract
      _rawContent
      partner {
        name
        url
        prefix
        logo {
          ...ImageWithPreview
        }
      }
      partner_url
      featured_image {
        image {
          ...ImageWithPreview
          asset {
            publicUrl
          }
        }
        caption
      }
      sport {
        slug {
          current
        }
        color
        name
      }
      web_article_type
      country {
        abbreviation
      }
    }
    guides: allSanityArticle(
      filter: {
        web_article_type: { eq: "guide" }
        sport: { elemMatch: { slug: { current: { eq: $sport } } } }
        publishing: { in: "website" }
      }
    ) {
      nodes {
        id
        title
        detailed_title
        country {
          abbreviation
        }
        featured_image {
          image {
            ...ImageWithPreview
          }
        }
        web_article_type
        slug {
          current
        }
      }
    }
    general_guides: allSanityArticle(
      filter: { id: { in: $general_guides } }
      sort: { date: DESC }
    ) {
      nodes {
        id
        title
        detailed_title
        country {
          abbreviation
        }
        featured_image {
          image {
            ...ImageWithPreview
          }
        }
        web_article_type
        slug {
          current
        }
      }
    }
    allSanityFaqGlossary(
      filter: { sport: { slug: { current: { eq: $sport } } } }
    ) {
      nodes {
        id
        sport {
          name
          slug {
            current
          }
        }
        featured_image {
          ...ImageWithPreview
        }
        type
      }
    }
    general_faqglossary: allSanityFaqGlossary(
      filter: { id: { in: $general_faqglossary } }
    ) {
      nodes {
        id
        sport {
          name
          slug {
            current
          }
        }
        featured_image {
          ...ImageWithPreview
        }
        type
      }
    }
  }
`
